import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import axios from 'axios';
import { 
  LayoutDashboard, Users, ClipboardList, Dumbbell, Menu, X, LogOut, Globe, Sparkles, Zap, MessageSquare, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainerLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const { logout } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/chat/unread-count`);
        setUnreadChat(r.data?.unread || 0);
      } catch {
        // silencioso
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => clearInterval(id);
  }, [location.pathname]);

  const navItems = [
    { path: '/trainer', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/trainer/clients', icon: Users, label: 'Mis Clientes' },
    { path: '/trainer/chat', icon: MessageSquare, label: 'Mensajes', badge: unreadChat },
    { path: '/trainer/plans', icon: ClipboardList, label: 'Mis Planes' },
    { path: '/trainer/exercises', icon: Dumbbell, label: 'Ejercicios' },
    { path: '/trainer/pildoras', icon: Sparkles, label: 'Píldoras del Día' },
    { path: '/trainer/automation', icon: Zap, label: 'Reglas Guardián' },
    { path: '/pricing', icon: Tag, label: 'Planes y precios' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/trainer') return location.pathname === '/trainer';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden glass-header fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
          <Menu className="h-6 w-6" />
        </Button>
        <img 
          src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg" 
          alt="GuardiansFit" className="h-10"
        />
        <Button variant="ghost" size="icon" onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}>
          <Globe className="h-5 w-5" />
        </Button>
      </header>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/80 z-50" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed top-0 left-0 z-50 h-screen w-64 sidebar transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="h-20 flex items-center justify-between px-6 border-b border-white/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg" 
              alt="GuardiansFit" className="h-12 logo-container"
            />
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="px-4 py-2">
            <span className="text-xs font-bold text-primary/80 uppercase tracking-widest">Entrenador</span>
          </div>

          <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm transition-all ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`}
                data-testid={`trainer-nav-${item.path.split('/').pop() || 'dashboard'}`}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                    data-testid={`trainer-nav-badge-${item.path.split('/').pop()}`}
                  >
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10 space-y-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
            >
              <Globe className="h-4 w-4 mr-3" />
              {language === 'es' ? 'Espa\u00f1ol' : 'English'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-red-400"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default TrainerLayout;
